export type AuthErrorFlow = "sign-in" | "sign-up" | "oauth";

export interface FriendlyAuthErrorOptions {
  fallback: string;
  flow: AuthErrorFlow;
}

export const getAuthErrorMessage = (
  error: unknown,
  { fallback, flow }: FriendlyAuthErrorOptions
): string => {
  if (typeof error !== "object" || error === null) {
    return fallback;
  }

  if (!("code" in error) || typeof error.code !== "string") {
    return fallback;
  }

  const code = error.code;

  switch (code) {
    case "auth/email-already-in-use":
    case "auth/credential-already-in-use":
      return flow === "sign-in"
        ? "An account with this email already exists. Enter your password to sign in."
        : "An account with this email already exists. Try signing in instead.";
    case "auth/invalid-credential":
    case "auth/wrong-password":
      if (flow === "sign-up") {
        return "An account with this email already exists. Sign in or reset your password if you've forgotten it.";
      }

      if (flow === "oauth") {
        return "That sign-in method didn't work. Try again or use a different sign-in option.";
      }

      return "Incorrect email or password.";
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/weak-password":
      return "Choose a stronger password and try again.";
    case "auth/too-many-requests":
      return "Too many attempts. Please wait a moment and try again.";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "Sign-in popup was closed.";
    case "auth/popup-blocked":
      return "Your browser blocked the sign-in popup. Please allow popups and try again.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    case "auth/account-exists-with-different-credential":
      return "An account with this email already uses a different sign-in method. Try the other option you signed up with.";
    default:
      break;
  }

  return fallback;
};

export const toFriendlyAuthError = (error: unknown, options: FriendlyAuthErrorOptions): Error =>
  new Error(getAuthErrorMessage(error, options), { cause: error });

export const isPopupClosedAuthError = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  "code" in error &&
  (error.code === "auth/popup-closed-by-user" || error.code === "auth/cancelled-popup-request");
